import React, {useEffect, useState, useRef} from "react";
import PropTypes from 'prop-types';
import {hot} from "react-hot-loader";
import css from "./App.css";
import Footer from 'common/Footer';
import StyledButton from 'common/StyledButton';
import StickyFooterLayout from 'common/StickyFooterLayout';
import Switch from 'common/Switch';
import useStarfishAPI from 'hooks/useStarfishAPI';
import Toast from 'common/Toast';
import ShowItemsOnDashboardOptions from "./ShowItemsOnDashboardOptions";
import { getWord } from '../utils/tenant';

const URL = '../api/student-view-configurations';

const defaultStudentViewConfig = {
  landingPage: "SUCCESS_NETWORK",
  showDashboard: false,
  showCalendarWidget: true,
  showNotificationWidget: true,
  showConnectionWidget: true,
  showServiceWidget: true,
}

const App = ({redirectTo, tenantId, showRequestHelpUrl, showStudentIntakeUrl}) => {
  const [toastMessage, setToastMessage] = useState("");
  const [responseWrite, callWriteAPI] = useStarfishAPI({ isNewAPI: true });
  const [responseRead, callReadAPI] = useStarfishAPI({ isNewAPI: true });
  const [studentViewConfig, setStudentViewConfig] = useState(defaultStudentViewConfig);
  const savedConfig = useRef(studentViewConfig);

  const clear = () => {
    setStudentViewConfig(savedConfig.current);
  };

  const submit = () => {
    callWriteAPI(URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(studentViewConfig)
    });
  };

  const toggleWidget = (widgetConfiguration, checked) => {
    setStudentViewConfig({...studentViewConfig, [widgetConfiguration]: checked});
  }

  useEffect(() => {
    callReadAPI(URL + '?tenantId=' + tenantId, {
      method: "GET",
      headers: {
        Pragma: "no-cache",
        "Cache-Control": "no-cache",
        Expires: "-1"
      }
    });
  }, []);

  useEffect(() => {
    if (responseRead.success) {
      setStudentViewConfig(responseRead.data);
      savedConfig.current = responseRead.data;
    } else if (responseRead.error) {
      setToastMessage(responseRead.error);
    }
  }, [responseRead]);

  useEffect(() => {
    if (responseWrite.error) {
      setToastMessage(responseWrite.error);
    } else if (responseWrite.success) {
      savedConfig.current = responseWrite.data;
      setToastMessage("Configuration successfully updated.");
    }
  }, [responseWrite]);

  const getShowStudentDashboard = () => (
        <div className={css["show-student-dashboard"]} data-role="showStudentDashboardSwitch">
          <Switch aria-labelledby="showStudentDashboard"
                  checked={studentViewConfig.showDashboard}
                  onChange={(checked) => {
                    if (checked) {
                      setStudentViewConfig({...studentViewConfig, showDashboard: checked});
                    } else {
                      setStudentViewConfig({...studentViewConfig, showDashboard: checked, landingPage: "SUCCESS_NETWORK"})
                    }
                  }} />
          <label id="showStudentDashboard">Show the Dashboard to students</label>
        </div>
    );

  const setRadioAsDashboard = () => setStudentViewConfig({...studentViewConfig, landingPage: "DASHBOARD"});
  const setRadioAsSuccessNetwork = () => setStudentViewConfig({...studentViewConfig, landingPage: "SUCCESS_NETWORK"});

  const getLandingPageOptions = () => (
      <div className={css["landing-page-container"]}>
        <div className={css["section-title"]}>Choose landing page for all students:</div>
        <div className={css["landing-page-options"]}>
          <div>
            <input type="radio"
                   aria-labelledby="dashboardLandingPageOption"
                   name="landing-page-option"
                   value="DASHBOARD"
                   onChange={setRadioAsDashboard}
                   checked={studentViewConfig.landingPage === 'DASHBOARD'} />
            <span id="dashboardLandingPageOption" className={css["clickable-radio-label"]} onClick={setRadioAsDashboard}>Dashboard</span>
          </div>
          <div>
            <input type="radio"
                   aria-labelledby="successNetworkLandingPageOption"
                   name="landing-page-option"
                   value="SUCCESS_NETWORK"
                   onChange={setRadioAsSuccessNetwork}
                   checked={studentViewConfig.landingPage === 'SUCCESS_NETWORK'} />
            <span id="successNetworkLandingPageOption" className={css["clickable-radio-label"]} onClick={setRadioAsSuccessNetwork}>
              My Success Network
              <div className={css["landing-page-option-detail"]}>This is automatically selected if you do not display the Dashboard.</div>
            </span>
          </div>
        </div>
      </div>
  );

  const externalUrl = (showUrl, adminTab, customLanguageWord) => {
    if (showUrl)
      return (<li><StyledButton className={css['redirect-link']} aria-label={`Redirects to ${getWord(customLanguageWord, false)} configuration`} asLink onClick={() => redirectTo({activeGroup: "admin-menu-configuration", activeTab: adminTab})}>{getWord(customLanguageWord, false)}</StyledButton></li>)
    else
      return (<li><span className={css['disabled-link']}>{getWord(customLanguageWord, false)} (disabled in your tenant)</span></li>)
  }

  const getRelatedConfigurations = () => (
      <div className={css["related-config-container"]}>
        <div className={css["section-title"]}>Related feature configurations:</div>
        <ul>
          {externalUrl(showRequestHelpUrl, 'configuration.request-help', 'studentHelp')}
          {externalUrl(showStudentIntakeUrl, 'configuration.student-intake', 'studentIntake')}
          <li><StyledButton className={css['redirect-link']} aria-label={"Redirects to System Announcement configuration"} asLink onClick={() => redirectTo({activeGroup: "admin-menu-system-settings", activeTab: "system-settings.system-announcement"})}>System Announcements</StyledButton></li>
        </ul>
      </div>
  );

  const getContent = () => (
        <div className={css["container"]}>
          <div className={css["header"]}>
            <div className={css["title"]}>Student View</div>
            <hr/>
            {getShowStudentDashboard()}
            {studentViewConfig.showDashboard && getLandingPageOptions()}
            {studentViewConfig.showDashboard &&
              <ShowItemsOnDashboardOptions
                toggleWidget={toggleWidget}
                studentViewConfig={studentViewConfig}
              />
            }
            {getRelatedConfigurations()}
          </div>
          {toastMessage && <Toast message={toastMessage} onTimeout={() => setToastMessage("")} />}
        </div>
    );

  const getFooter = () => {
    return (
        <Footer
            primaryButton={<StyledButton onClick={submit}>SAVE CHANGES</StyledButton>}
            secondaryButton={<StyledButton asLink onClick={clear}>CANCEL</StyledButton>}
        />
    )
  };

  return <StickyFooterLayout content={getContent()} footer={getFooter()} />

};

App.propTypes = {
  redirectTo: PropTypes.func.isRequired,
  tenantId: PropTypes.number.isRequired,
  showRequestHelpUrl: PropTypes.bool,
  showStudentIntakeUrl: PropTypes.bool
};

export default hot(module)(App);
